import NoSsr from "@mui/material/NoSsr";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useItemTooltip, useMouseTracker } from "@mui/x-charts/ChartsTooltip";
import { generateVirtualElement } from "./generateVirtualElement";
import { Box } from "@mui/system";

interface CustomItemTooltipProps {
  dataset?: any[];
}

export function CustomTooltipWithDateAndValue({ dataset }: CustomItemTooltipProps = {}) {
  const tooltipData = useItemTooltip();
  const mousePosition = useMouseTracker();

  if (!tooltipData || !mousePosition) {
    return null;
  }

  const isMousePointer = mousePosition?.pointerType === "mouse";
  const yOffset = isMousePointer ? 0 : 40 - mousePosition.height;

  // Check the series type from identifier
  const seriesType = (tooltipData.identifier as any)?.type;
  const isBarTooltip = seriesType === "bar";
  const isLineTooltip = tooltipData.label === "Daily Number of Competitors" || seriesType === "line";

  // Get the current data point from the dataset
  let competitorCount;
  let dateValue;
  
  if (dataset && dataset.length > 0 && tooltipData.identifier) {
    const dataIndex = (tooltipData.identifier as any).dataIndex;
    
    if (dataIndex !== undefined && dataset[dataIndex]) {
      const currentDataPoint = dataset[dataIndex];
      
      // For bar chart, get the date
      if (isBarTooltip && currentDataPoint.date) {
        // Handle both formats: "2026-01-19T..." and "2026-01-19"
        dateValue = currentDataPoint.date.split("T")[0];
      }
      
      // For line series, get competitor count
      if (isLineTooltip && currentDataPoint.num_competitors !== undefined) {
        competitorCount = currentDataPoint.num_competitors;
      }
    }
  }

  return (
    <NoSsr>
      <Popper
        sx={{
          pointerEvents: "none",
          zIndex: (theme) => theme.zIndex.modal,
        }}
        open
        placement={isMousePointer ? "bottom" : "top"}
        anchorEl={generateVirtualElement(mousePosition)}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, yOffset],
            },
          },
        ]}
      >
        <Paper
          elevation={0}
          sx={{
            m: 1,
            p: 1.5,
            border: "solid",
            borderWidth: 0,
            borderColor: "divider",
            boxShadow: "0px 0px 3px #00000073",
            position: "relative",
          }}
        >
          <Stack
            direction="column"
            alignItems="flex-start"
            sx={{ paddingLeft: "0px", paddingRight: "0px" }}
          >
            <Typography fontWeight="light" fontSize={18}>
              {tooltipData.formattedValue}
            </Typography>
            {isBarTooltip && dateValue && (
              <Typography
                fontWeight="light"
                fontSize={14}
                sx={{ color: "#666", mt: 0.5 }}
              >
                Date: {dateValue}
              </Typography>
            )}
            {isLineTooltip && competitorCount !== undefined && (
              <Typography
                fontWeight="light"
                fontSize={14}
                sx={{ color: "#666", mt: 0.5 }}
              >
                Daily Number of Competitors: {competitorCount}
              </Typography>
            )}
          </Stack>
          <Box
            style={{
              position: "absolute",
              transform: "rotate(90deg)",
              top: "-10px",
              right: "45%",
              zIndex: "1",
              width: 0,
              height: 0,
              borderTop: "8px solid transparent",
              borderBottom: "8px solid transparent",
              borderRight: "8px solid #fff",
            }}
          />
        </Paper>
      </Popper>
    </NoSsr>
  );
}